export const endpoints = {
	authentication: {
		login: '/auth/login',
		me: '/auth/me',
	},
	hotels: {
		index: 'hotel',
		item: (id: string | number) => `hotel/${id}`,
	},
	brands: {
		index: 'brand',
	},
	regions: {
		index: 'region',
	},
	featureFlags: {
		check: '/features/check',
	},
	myLQA: {
		plans: {
			index: 'my-lqa/plans',
			item: (id: string) => `/my-lqa/plans/${id}`,
		},
		pricing: {
			brands: 'my-lqa/pricing/brands',
			hotels: 'my-lqa/pricing/hotels',
		},
		tiers: {
			index: 'my-lqa/tiers',
			item: (id: string) => `/my-lqa/tiers/${id}`,
		},
		tierVariants: {
			index: 'my-lqa/tier-variants',
			oneActivePerTier: 'my-lqa/tier-variants/active',
			item: (id: string) => `/my-lqa/tier-variants/${id}`,
		},
		contracts: {
			index: 'my-lqa/contracts',
			export: 'my-lqa/contracts/export',
			statistics: 'my-lqa/contracts/statistics',
			trial: 'my-lqa/contracts/trial',
			item: (id: string | number) => `/my-lqa/contracts/${id}`,
			periods: (id: string | number) => `/my-lqa/contracts/${id}/periods`,
			activityLogs: (id: string | number) =>
				`/my-lqa/contracts/${id}/activity-logs`,
			licensedHotels: (id: string | number) =>
				`/my-lqa/contracts/${id}/licensed-hotels`,
			licensedHotel: (contractId: string | number, hotelId: string | number) =>
				`/my-lqa/contracts/${contractId}/licensed-hotels/${hotelId}`,
			hotelOverview: (hotelId: string | number) =>
				`/my-lqa/contracts/hotel-overview/${hotelId}`,
		},
		billingAdminOverview: {
			index: '/my-lqa/billing-admin-overview',
		},
		invoices: {
			index: 'my-lqa/invoices',
			item: (id: string | number) => `/my-lqa/invoices/${id}`,
		},
		pricingConfigs: {
			index: 'my-lqa/pricing-configs',
		},
	},
	stripe: {
		customers: {
			index: (customerType: string, customerId: number) =>
				`/integrations/stripe/customers/${customerType}/${customerId}`,
		},
		paymentMethods: {
			index: (customerType: string, customerId: number) =>
				`/integrations/stripe/customers/${customerType}/${customerId}/payment-methods`,
			setupIntent: (customerType: string, customerId: number) =>
				`/integrations/stripe/customers/${customerType}/${customerId}/payment-methods/setup-intent`,
			pendingVerification: (customerType: string, customerId: number) =>
				`/integrations/stripe/customers/${customerType}/${customerId}/payment-methods/pending-verification`,
		},
		invoices: {
			index: (customerType: string, customerId: number) =>
				`/integrations/stripe/invoices/${customerType}/${customerId}`,
		},
	},
	myAuditTrends: {
		brands: {
			index: '/my-audit-trends/brands',
		},
		hotels: {
			byBrand: (brandId: string | number) =>
				`/my-audit-trends/brands/${brandId}/hotels`,
		},
		checklists: {
			scoreTimeline: (id: string | number) =>
				`/my-audit-trends/checklists/${id}/score-timeline`,
			missedQuestions: (id: string | number) =>
				`/my-audit-trends/checklists/${id}/missed-questions`,
			summary: (id: string | number) =>
				`/my-audit-trends/checklists/${id}/summary`,
			filters: (id: string | number) =>
				`/my-audit-trends/checklists/${id}/filters`,
			downloadExcel: (id: string | number) =>
				`my-audit-trends/checklists/${id}/excel`,
			downloadPdf: (id: string | number) =>
				`my-audit-trends/checklists/${id}/pdf`,
			submissions: (id: string | number) =>
				`my-audit-trends/checklists/${id}/submissions`,
			auditSections: (id: string | number, auditId: string | number) =>
				`/my-audit-trends/checklists/${id}/audits/${auditId}/sections`,
			auditPdf: (id: string | number, auditId: string | number) =>
				`/my-audit-trends/checklists/${id}/audits/${auditId}/pdf`,
			auditSectionDetail: (
				id: string | number,
				auditId: string | number,
				sectionId: string | number
			) =>
				`/my-audit-trends/checklists/${id}/audits/${auditId}/sections/${sectionId}`,
			auditSectionStandards: (
				id: string | number,
				auditId: string | number,
				sectionId: string | number
			) =>
				`/my-audit-trends/checklists/${id}/audits/${auditId}/sections/${sectionId}/standards`,
		},
		checklistsByTrends: {
			index: '/my-audit-trends/checklists-by-trends',
			excel: '/my-audit-trends/checklists-by-trends/excel',
		},
		departments: {
			index: '/my-audit-trends/fetch-departments',
		},
	},
	workspace: {
		brands: {
			index: '/my-audit-trends/brands',
			hotels: (brandId: string | number) =>
				`/my-audit-trends/brands/${brandId}/hotels`,
		},
	},
	selfAssessmentTrends: {
		brands: {
			index: '/customer/brands',
		},
		hotels: {
			byBrand: (brandId: string | number) =>
				`/customer/brands/${brandId}/hotels`,
		},
		filters: {
			byBrand: (brandId: string | number) =>
				`/self-assessment-trends/brand/${brandId}/filters`,
			byHotel: (hotelId: string | number) =>
				`/self-assessment-trends/hotel/${hotelId}/filters`,
		},
		summary: {
			byBrand: (brandId: string | number) =>
				`/self-assessment-trends/brand/${brandId}/summary`,
			byHotel: (hotelId: string | number) =>
				`/self-assessment-trends/hotel/${hotelId}/summary`,
		},
		scoreTimeline: {
			byBrand: (brandId: string | number) =>
				`/self-assessment-trends/brand/${brandId}/score-timeline`,
			byHotel: (hotelId: string | number) =>
				`/self-assessment-trends/hotel/${hotelId}/score-timeline`,
		},
		missedStandards: {
			byBrand: (brandId: string | number) =>
				`/self-assessment-trends/brand/${brandId}/missed-standards`,
			byHotel: (hotelId: string | number) =>
				`/self-assessment-trends/hotel/${hotelId}/missed-standards`,
		},
		downloadPdf: {
			byBrand: (brandId: string | number) =>
				`/self-assessment-trends/brand/${brandId}/pdf`,
			byHotel: (hotelId: string | number) =>
				`/self-assessment-trends/hotel/${hotelId}/pdf`,
		},
		downloadExcel: {
			byBrand: (brandId: string | number) =>
				`/self-assessment-trends/brand/${brandId}/excel`,
			byHotel: (hotelId: string | number) =>
				`/self-assessment-trends/hotel/${hotelId}/excel`,
		},
	},
	roster: {
		index: '/v2/roster',
		detail: (rosterId: number) => `/v2/roster/${rosterId}`,
		overview: (consultantId: number) => `/v2/roster/${consultantId}/overview`,
		statuses: (consultantId: number) => `/v2/roster/${consultantId}/statuses`,
		logs: (rosterId: number) => `/v2/roster/${rosterId}/logs`,
		updateStatus: (rosterId: number) => `/v2/roster/update-status/${rosterId}`,
	},
	lqaUser: {
		index: '/lqaUser/',
	},
	client: {
		index: '/client',
	},
	customerRegions: {
		index: '/customerRegion',
	},
	templateCategories: {
		allCategories: '/template/all-categories',
	},
	userPreference: {
		viewConfig: (viewKey: string) => `/user-preference/view-config/${viewKey}`,
	},
	userSettings: {
		users: {
			index: 'user-settings/users',
			import: 'user-settings/users/import',
			importValidate: 'user-settings/users/import/validate',
			activate: 'user-settings/users/activate',
			deactivate: 'user-settings/users/deactivate',
			item: (id: string | number) => `/user-settings/users/${id}`,
		},
		clients: {
			index: 'user-settings/clients',
		},
		brands: {
			index: 'user-settings/brands',
		},
		regions: {
			index: 'user-settings/regions',
		},
		hotels: {
			index: 'user-settings/hotels',
		},
		departments: {
			index: 'user-settings/departments',
		},
	},
	hotelTags: {
		config: () => 'customerHotelTag/config',
		index: (hotelId: number) => `customerHotelTag/${hotelId}`,
		create: (hotelId: number) => `customerHotelTag/${hotelId}`,
		inactivate: (hotelId: number, tagId: number) =>
			`customerHotelTag/${hotelId}/${tagId}/inactivate`,
		reactivate: (hotelId: number, tagId: number) =>
			`customerHotelTag/${hotelId}/${tagId}/reactivate`,
		active: (hotelId: number) => `customerHotelTag/${hotelId}/active`,
	},
	customerRegion: {
		index: '/customerRegion',
	},
	customerCountry: {
		index: '/customerCountry',
	},
	customerLocation: {
		index: '/customerLocation',
	},
};
